import React, { useState, useEffect, useMemo } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users, FolderKanban, LogOut, ChevronLeft, ChevronRight, School, Wallet,
  Flower2, Package, ShieldCheck, ClipboardList, FileText, GraduationCap,
} from 'lucide-react';
import {
  mainNavItems, financeSubItems, personnelSubItems, studentDirectoryItems,
  assetsSubItems, schoolPrograms, womenPrograms, bottomNavItems, adminSubItems,
  SCHOOL_PROJECT_TYPES, WOMEN_PROJECT_TYPES,
} from '../../config/navigation';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { cn } from '../../lib/utils';
import {
  NavItem, SubNavItem, SidebarSection, CollapsedLink, ProjectSubNavItem,
} from './SidebarNavItems';
import { ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SUUBI_LOGO = "https://customer-assets.emergentagent.com/job_community-school-hub/artifacts/vxd2g9ou_suubi%20final_Logo_00_00.jpg";

const slug = (label) => label.toLowerCase().replace(/\s+/g, '-');

const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [schoolsOpen, setSchoolsOpen] = useState(true);
  const [womenOpen, setWomenOpen] = useState(true);
  const [personnelOpen, setPersonnelOpen] = useState(true);
  const [studentDirOpen, setStudentDirOpen] = useState(true);
  const [financeOpen, setFinanceOpen] = useState(true);
  const [assetsOpen, setAssetsOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const [dynamicProjects, setDynamicProjects] = useState([]);
  const [dataEntryCount, setDataEntryCount] = useState(0);

  useEffect(() => {
    if (user) {
      axios.get(`${API_URL}/api/data-entry/forms`)
        .then((res) => setDataEntryCount(res.data.forms?.length || 0))
        .catch(() => setDataEntryCount(0));
    }
  }, [user]);

  useEffect(() => {
    // Only fetch projects once the user is authenticated
    if (user) {
      axios.get(`${API_URL}/api/projects`)
        .then(res => {
          // Exclude the programs already in the dropdown (loans/maize/sponsorship/training covered separately)
          const excludedTypes = ['loans_program', 'maize_buyback', 'child_sponsorship', 'vocational_training'];
          setDynamicProjects(res.data.filter(p => !excludedTypes.includes(p.project_type)));
        })
        .catch(() => setDynamicProjects([]));
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isPersonnelPage = location.pathname.startsWith('/personnel') || location.pathname.startsWith('/sis/students');
  const isFinancePage = ['/finance', '/income', '/expenses'].some(p => location.pathname.startsWith(p));
  const isAssetsPage = location.pathname.startsWith('/assets');
  const isAdminPage = location.pathname.startsWith('/admin');
  const isSuperAdmin = user?.role === 'super_admin';

  const hasStudentDirAccess = hasPermission('students', 'view');

  const dynamicSchoolProjects = dynamicProjects.filter(p => SCHOOL_PROJECT_TYPES.includes(p.project_type));
  const dynamicWomenProjects = dynamicProjects.filter(p => WOMEN_PROJECT_TYPES.includes(p.project_type));
  const dynamicOtherProjects = dynamicProjects.filter(p =>
    !SCHOOL_PROJECT_TYPES.includes(p.project_type) && !WOMEN_PROJECT_TYPES.includes(p.project_type)
  );

  // Memoize permission-filtered nav lists — navigation mirrors the user's
  // permissions_v2 matrix (see config/navigation.js `perm` keys).
  const filteredMainItems = useMemo(() => mainNavItems.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);
  const filteredFinanceItems = useMemo(() => financeSubItems.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);
  const filteredPersonnelItems = useMemo(() => personnelSubItems.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);
  const filteredAssetsItems = useMemo(() => assetsSubItems.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);
  const filteredSchoolPrograms = useMemo(() => schoolPrograms.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);
  const filteredWomenPrograms = useMemo(() => womenPrograms.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);
  const filteredBottomItems = useMemo(() => bottomNavItems.filter(item => hasPermission(item.perm, 'view')), [hasPermission]);

  const isSchoolsPage = ['/sponsorship', '/training', '/school-clubs', '/catchup'].some(p => location.pathname.startsWith(p)) ||
    dynamicSchoolProjects.some(p => location.pathname === `/projects/${p.id}`);
  const isWomenPage = ['/loans', '/maize'].some(p => location.pathname.startsWith(p)) ||
    dynamicWomenProjects.some(p => location.pathname === `/projects/${p.id}`);
  const hasSchoolsAccess = filteredSchoolPrograms.length > 0 || (dynamicSchoolProjects.length > 0 && hasPermission('projects', 'view'));
  const hasWomenAccess = filteredWomenPrograms.length > 0 || (dynamicWomenProjects.length > 0 && hasPermission('projects', 'view'));
  const hasOtherProjectsAccess = dynamicOtherProjects.length > 0 && hasPermission('projects', 'view');
  const hasPersonnelAccess = filteredPersonnelItems.length > 0;
  const hasFinanceAccess = filteredFinanceItems.length > 0;
  const hasAssetsAccess = filteredAssetsItems.length > 0;

  return (
    <aside
      className={cn(
        "h-screen bg-[#1B5E20] text-white flex flex-col transition-all duration-300 sticky top-0",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo */}
      <div className="p-3 flex items-center justify-between border-b border-white/10">
        {!collapsed && (
          <div className="flex items-center gap-3">
            <img src={SUUBI_LOGO} alt="Suubi Logo" className="h-10 w-10 rounded-full object-cover bg-white" />
            <div className="flex flex-col">
              <span className="font-heading font-bold text-base leading-tight">SUUBI</span>
              <span className="text-[10px] text-white/70 leading-tight">Community Dev. Org</span>
            </div>
          </div>
        )}
        {collapsed && (
          <img src={SUUBI_LOGO} alt="Suubi Logo" className="h-9 w-9 rounded-full object-cover bg-white mx-auto" />
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className={cn("text-white hover:bg-white/10", collapsed && "hidden")}
          data-testid="sidebar-toggle"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>

      {collapsed && (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="text-white hover:bg-white/10 mx-auto mt-2"
          data-testid="sidebar-expand"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <nav className="space-y-1 px-2">
          {filteredMainItems.map((item) => (
            <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} collapsed={collapsed} testId={`nav-${slug(item.label)}`} />
          ))}

          {/* Data Entry Center (visible if user has access to at least one form) */}
          {dataEntryCount > 0 && (
            <>
              <NavItem to="/data-entry" icon={ClipboardList} label="Data Entry" collapsed={collapsed} testId="nav-data-entry" badge={dataEntryCount} />
              <NavItem to="/requisitions" icon={FileText} label="Requisitions" collapsed={collapsed} testId="nav-requisitions" />
            </>
          )}

          {/* Finance */}
          {hasFinanceAccess && !collapsed && (
            <SidebarSection open={financeOpen} onOpenChange={setFinanceOpen} icon={Wallet} label="Finance" active={isFinancePage} testId="nav-finance-dropdown">
              {filteredFinanceItems.map((item) => (
                <SubNavItem key={item.path} to={item.path} icon={item.icon} label={item.label} testId={`nav-finance-${item.label.toLowerCase()}`} />
              ))}
            </SidebarSection>
          )}
          {hasFinanceAccess && collapsed && (
            <CollapsedLink to="/finance/income" active={isFinancePage} icon={Wallet} />
          )}

          {/* Personnel */}
          {hasPersonnelAccess && !collapsed && (
            <SidebarSection open={personnelOpen} onOpenChange={setPersonnelOpen} icon={Users} label="Personnel" active={isPersonnelPage} testId="nav-personnel-dropdown">
              {filteredPersonnelItems.map((item) => (
                <SubNavItem key={item.path} to={item.path} icon={item.icon} label={item.label} testId={`nav-personnel-${item.label.toLowerCase()}`} />
              ))}
              {hasStudentDirAccess && (
                <Collapsible open={studentDirOpen} onOpenChange={setStudentDirOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg transition-all ml-4 w-[calc(100%-1rem)] hover:bg-white/10 text-sm",
                        location.pathname.startsWith('/sis/students') && "bg-white/10 font-medium"
                      )}
                      data-testid="nav-student-directory-dropdown"
                    >
                      <School className="h-4 w-4 flex-shrink-0" />
                      <span className="flex-1 text-left">Student Directory</span>
                      <ChevronDown className={cn("h-4 w-4 transition-transform", studentDirOpen && "rotate-180")} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-1 mt-1">
                    {studentDirectoryItems.map((item) => (
                      <SubNavItem key={item.path} to={item.path} icon={GraduationCap} label={item.label} testId={`nav-${slug(item.label)}`} indent="ml-9" />
                    ))}
                  </CollapsibleContent>
                </Collapsible>
              )}
            </SidebarSection>
          )}
          {hasPersonnelAccess && collapsed && (
            <CollapsedLink to="/personnel/staff" active={isPersonnelPage} icon={Users} />
          )}

          {/* Assets & Inventory */}
          {hasAssetsAccess && !collapsed && (
            <SidebarSection open={assetsOpen} onOpenChange={setAssetsOpen} icon={Package} label="Assets & Inventory" active={isAssetsPage} testId="nav-assets-dropdown">
              {filteredAssetsItems.map((item) => (
                <SubNavItem key={item.path} to={item.path} icon={item.icon} label={item.label} testId={`nav-assets-${slug(item.label)}`} />
              ))}
            </SidebarSection>
          )}
          {hasAssetsAccess && collapsed && (
            <CollapsedLink to="/assets/dashboard" active={isAssetsPage} icon={Package} />
          )}

          {/* SCHOOLS Umbrella */}
          {hasSchoolsAccess && !collapsed && (
            <SidebarSection open={schoolsOpen} onOpenChange={setSchoolsOpen} icon={School} label="Schools" active={isSchoolsPage} testId="nav-schools-dropdown">
              {filteredSchoolPrograms.map((item) => (
                <SubNavItem key={item.path} to={item.path} icon={item.icon} label={item.label} testId={`nav-${slug(item.label)}`} />
              ))}
              {dynamicSchoolProjects.map((p) => (
                <ProjectSubNavItem key={p.id} project={p} testId={`nav-school-project-${p.id}`} />
              ))}
            </SidebarSection>
          )}

          {/* WOMEN'S Programs Umbrella */}
          {hasWomenAccess && !collapsed && (
            <SidebarSection open={womenOpen} onOpenChange={setWomenOpen} icon={Flower2} label="Women's Programs" active={isWomenPage} testId="nav-women-dropdown">
              {filteredWomenPrograms.map((item) => (
                <SubNavItem key={item.path} to={item.path} icon={item.icon} label={item.label} testId={`nav-${slug(item.label)}`} />
              ))}
              {dynamicWomenProjects.map((p) => (
                <ProjectSubNavItem key={p.id} project={p} testId={`nav-women-project-${p.id}`} />
              ))}
            </SidebarSection>
          )}

          {/* Other Projects (uncategorised) */}
          {hasOtherProjectsAccess && !collapsed && (
            <SidebarSection open={projectsOpen} onOpenChange={setProjectsOpen} icon={FolderKanban} label="Other Projects" testId="nav-other-projects-dropdown">
              {dynamicOtherProjects.map((p) => (
                <ProjectSubNavItem key={p.id} project={p} testId={`nav-other-project-${p.id}`} />
              ))}
            </SidebarSection>
          )}

          {/* Collapsed view: combined projects icon */}
          {(hasSchoolsAccess || hasWomenAccess) && collapsed && (
            <CollapsedLink to="/loans" active={isSchoolsPage || isWomenPage} icon={FolderKanban} />
          )}

          {/* Super Admin Section (super_admin only) */}
          {isSuperAdmin && !collapsed && (
            <SidebarSection open={adminOpen} onOpenChange={setAdminOpen} icon={ShieldCheck} label="Super Admin" active={isAdminPage} testId="nav-admin-dropdown">
              {adminSubItems.map((item) => (
                <SubNavItem key={item.path} to={item.path} icon={item.icon} label={item.label} testId={`nav-admin-${slug(item.label)}`} />
              ))}
            </SidebarSection>
          )}
          {isSuperAdmin && collapsed && (
            <CollapsedLink to="/admin/users" active={isAdminPage} icon={ShieldCheck} />
          )}

          {/* Bottom Items */}
          {filteredBottomItems.map((item) => (
            <NavItem key={item.path} to={item.path} icon={item.icon} label={item.label} collapsed={collapsed} testId={`nav-${item.label.toLowerCase()}`} />
          ))}
        </nav>
      </ScrollArea>

      {/* User & Logout */}
      <div className="p-4 border-t border-white/10">
        {!collapsed && (
          <div className="mb-3 px-2">
            <p className="font-medium truncate">{user?.full_name}</p>
            <p className="text-xs text-white/60 capitalize">{user?.role?.replace('_', ' ')}</p>
          </div>
        )}
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className={cn("w-full text-white hover:bg-white/10", collapsed && "px-2")}
        >
          <LogOut className="h-5 w-5" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>
    </aside>
  );
};

export default Sidebar;
